// ─────────────────────────────────────────────────────────────────
// Helpers compartilhados do módulo CRM (Pipeline / DealCard / List)
// Sprint 8C-1 — extraído de KanbanBoard.tsx + DealsPage.tsx
// ─────────────────────────────────────────────────────────────────

import type { LucideIcon } from "lucide-react";
import { Phone, MessageCircle, Mail, Users, ListTodo } from "lucide-react";
import { formatDistanceToNow, isToday, isTomorrow, isPast, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { DealWithLead, PipelineStage } from "@/hooks/useDeals";

export type { DealWithLead, PipelineStage };

// ── Currency ────────────────────────────────────────────
export function formatCurrency(value: number | null | undefined): string {
  if (value == null || value === 0) return "R$ 0";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// ── Days in stage ───────────────────────────────────────
export function daysInStage(deal: DealWithLead): number {
  const ref = (deal as any).entered_stage_at || deal.created_at;
  return Math.floor((Date.now() - new Date(ref).getTime()) / (1000 * 60 * 60 * 24));
}

// ── Source tag (lead origin) ────────────────────────────
const sourceLabels: Record<string, { label: string; color: string }> = {
  whatsapp: { label: "WhatsApp", color: "bg-emerald-500/10 text-emerald-700" },
  site: { label: "Site", color: "bg-blue-500/10 text-blue-700" },
  landing_page: { label: "Landing Page", color: "bg-violet-500/10 text-violet-700" },
  indicacao: { label: "Indicação", color: "bg-amber-500/10 text-amber-700" },
  facebook: { label: "Facebook", color: "bg-blue-600/10 text-blue-700" },
  instagram: { label: "Instagram", color: "bg-pink-500/10 text-pink-700" },
  google: { label: "Google Ads", color: "bg-red-500/10 text-red-700" },
  manual: { label: "Manual", color: "bg-muted text-muted-foreground" },
};

export function getSourceTag(source: string | null) {
  if (!source) return null;
  return sourceLabels[source] || { label: source, color: "bg-muted text-muted-foreground" };
}

// ── Phone formatting (BR) ───────────────────────────────
export function formatPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  // (XX) XXXXX-XXXX  ou  (XX) XXXX-XXXX
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone.length > 18 ? phone.slice(0, 18) + "…" : phone;
}

// Normaliza para wa.me (com DDI 55)
export function normalizePhoneForWhatsApp(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) return null;
  return digits.startsWith("55") ? digits : `55${digits}`;
}

// ── Probability color (semantic tokens) ─────────────────
export type ProbabilityClasses = { container: string; dot: string };

export function getProbabilityColor(probability: number | null | undefined): ProbabilityClasses {
  const p = probability ?? 0;
  if (p > 70) return { container: "bg-success/15 text-success", dot: "bg-success" };
  if (p >= 40) return { container: "bg-info/15 text-info", dot: "bg-info" };
  return { container: "bg-warning/15 text-warning", dot: "bg-warning" };
}

// ── Activity icon by type ───────────────────────────────
export function getActivityIcon(type: string | null | undefined): LucideIcon {
  switch (type) {
    case "call":
      return Phone;
    case "whatsapp":
      return MessageCircle;
    case "email":
      return Mail;
    case "meeting":
      return Users;
    default:
      return ListTodo;
  }
}

export function getActivityLabel(type: string | null | undefined): string {
  switch (type) {
    case "call":
      return "Ligar";
    case "whatsapp":
      return "WhatsApp";
    case "email":
      return "Enviar e-mail";
    case "meeting":
      return "Reunião";
    case "task":
      return "Tarefa";
    default:
      return "Atividade";
  }
}

// ── Relative date (pt-BR) ───────────────────────────────
export function formatRelativeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  if (isToday(date)) return `hoje ${format(date, "HH:mm")}`;
  if (isTomorrow(date)) return `amanhã ${format(date, "HH:mm")}`;
  if (isPast(date)) {
    return `atrasada ${formatDistanceToNow(date, { locale: ptBR, addSuffix: false })}`;
  }
  return formatDistanceToNow(date, { locale: ptBR, addSuffix: true });
}

// ── Stagnation bar fill (semantic) ──────────────────────
export function getStagnationFill(days: number, limit: number): { cls: string; widthPct: number } {
  const safeLimit = limit > 0 ? limit : 14;
  const ratio = Math.min(days / safeLimit, 1);
  const overLimit = days > safeLimit;
  const cls = overLimit ? "bg-destructive" : ratio <= 0.5 ? "bg-success" : "bg-warning";
  return { cls, widthPct: Math.max(ratio * 100, overLimit ? 100 : 8) };
}
